import { useEffect, useState } from 'react'
import { TIPP_GEWINNER, type AnsageTipp, type TippGewinner } from '@k4ia/shared'
import {
  ansageTonVorbereiten,
  createAnsage,
  deleteAnsage,
  deleteAnsageLogo,
  listAnsagen,
  updateAnsage,
  uploadAnsageLogo,
  type AdminAnsage,
} from '../lib/api'
import { FileButton } from '../components/ui'

const OHNE_TIPP: AnsageTipp = {
  aktiv: false,
  teamA: '',
  teamB: '',
  gewinner: 'a',
  ergebnis: '',
  text: '',
}

type Meldung = { art: 'ok' | 'fehler'; text: string }

/**
 * Ansagen (Flo, 31.10.): ein Text, ein Link. Wer den Link öffnet und K4ia
 * antippt, hört genau diesen Text — als Tonspur mit ihrer Stimme, nichts
 * formuliert das Modell. Für Begrüßungen, Hinweise, eine Durchsage am
 * Stand. Der Link ist eine zufällige Adresse: nicht zu erraten, aber ohne
 * Passwort — wer ihn hat, hört die Ansage.
 */
export function AnsagenPage() {
  const [ansagen, setAnsagen] = useState<AdminAnsage[] | null>(null)
  const [sprachausgabe, setSprachausgabe] = useState(true)
  const [meldung, setMeldung] = useState<Meldung | null>(null)
  const [busy, setBusy] = useState(false)
  const [neuTitel, setNeuTitel] = useState('')
  const [neuText, setNeuText] = useState('')

  const laden = async () => {
    const r = await listAnsagen()
    setAnsagen(r.ansagen)
    setSprachausgabe(r.sprachausgabe)
  }

  useEffect(() => {
    void laden().catch((e: unknown) => fehler(e))
  }, [])

  function fehler(e: unknown) {
    setMeldung({ art: 'fehler', text: e instanceof Error ? e.message : String(e) })
  }

  async function tun(arbeit: () => Promise<unknown>, ok?: string) {
    setBusy(true)
    setMeldung(null)
    try {
      await arbeit()
      await laden()
      if (ok) setMeldung({ art: 'ok', text: ok })
    } catch (e) {
      fehler(e)
    } finally {
      setBusy(false)
    }
  }

  async function anlegen() {
    if (!neuText.trim()) return
    await tun(async () => {
      await createAnsage({ title: neuTitel.trim(), text: neuText.trim(), tipp: OHNE_TIPP })
      setNeuTitel('')
      setNeuText('')
    }, 'Ansage angelegt — Link steht unten.')
  }

  return (
    <div className="max-w-3xl space-y-8">
      <div>
        <h2 className="page-title">Ansagen</h2>
        <p className="text-sm leading-relaxed text-text-muted">
          Ein Text, ein Link. Wer den Link öffnet und K4ia antippt, hört genau diesen Text mit ihrer
          Stimme — wörtlich, nichts Eigenes. Beträge und Abkürzungen so schreiben, wie K4ia sie
          sagen soll („Tausend Euro“, nicht „T€“). Der Link ist nicht zu erraten, aber offen: Wer
          ihn hat, hört die Ansage.
        </p>
      </div>

      {meldung && (
        <p
          role={meldung.art === 'fehler' ? 'alert' : 'status'}
          className={`text-sm ${meldung.art === 'fehler' ? 'text-status-error' : 'text-text-muted'}`}
        >
          {meldung.text}
        </p>
      )}
      {!sprachausgabe && (
        <p className="text-sm text-status-error">
          Sprachausgabe ist in dieser Umgebung nicht konfiguriert — die Seite zeigt den Text, K4ia
          bleibt stumm.
        </p>
      )}

      <section className="card space-y-3 p-5">
        <h3 className="font-semibold">Neue Ansage</h3>
        <input
          className="input"
          placeholder="Titel (optional, z. B. „Begrüßung Händlertag“)"
          value={neuTitel}
          onChange={(e) => setNeuTitel(e.target.value)}
          aria-label="Titel"
          disabled={busy}
        />
        <textarea
          className="input min-h-[8rem]"
          placeholder="Was K4ia sagen soll …"
          value={neuText}
          onChange={(e) => setNeuText(e.target.value)}
          aria-label="Text"
          disabled={busy}
        />
        <button
          type="button"
          className="btn-primary self-start"
          disabled={busy || !neuText.trim()}
          onClick={() => void anlegen()}
        >
          Ansage anlegen
        </button>
      </section>

      {ansagen === null ? (
        <p className="text-sm text-text-muted">Lädt …</p>
      ) : ansagen.length === 0 ? (
        <p className="card p-5 text-sm text-text-muted">Noch keine Ansage.</p>
      ) : (
        <ol className="space-y-3">
          {ansagen.map((a) => (
            <AnsageKarte
              key={a.id}
              ansage={a}
              busy={busy}
              sprachausgabe={sprachausgabe}
              onSpeichern={(w) => tun(() => updateAnsage(a.id, w), 'Gespeichert.')}
              onTon={() =>
                tun(async () => {
                  await ansageTonVorbereiten(a.id)
                  if (a.tipp.aktiv && a.tipp.text.trim()) await ansageTonVorbereiten(a.id, 'tipp')
                }, 'Tonspur liegt bereit.')
              }
              onLogo={(seite, file) => tun(() => uploadAnsageLogo(a.id, seite, file))}
              onLogoEntfernen={(seite) => tun(() => deleteAnsageLogo(a.id, seite))}
              onLoeschen={() => {
                if (
                  window.confirm(
                    `„${a.title || a.text.slice(0, 40)}“ löschen? Der Link geht dann ins Leere.`,
                  )
                )
                  void tun(() => deleteAnsage(a.id), 'Gelöscht.')
              }}
            />
          ))}
        </ol>
      )}
    </div>
  )
}

function AnsageKarte({
  ansage,
  busy,
  sprachausgabe,
  onSpeichern,
  onTon,
  onLogo,
  onLogoEntfernen,
  onLoeschen,
}: {
  ansage: AdminAnsage
  busy: boolean
  sprachausgabe: boolean
  onSpeichern: (w: { title: string; text: string; tipp: AnsageTipp }) => Promise<void>
  onTon: () => Promise<void>
  onLogo: (seite: 'a' | 'b', file: File) => Promise<void>
  onLogoEntfernen: (seite: 'a' | 'b') => Promise<void>
  onLoeschen: () => void
}) {
  const [title, setTitle] = useState(ansage.title)
  const [text, setText] = useState(ansage.text)
  const [tipp, setTipp] = useState<AnsageTipp>(ansage.tipp)
  const [kopiert, setKopiert] = useState(false)
  useEffect(() => {
    setTitle(ansage.title)
    setText(ansage.text)
    setTipp(ansage.tipp)
  }, [ansage])
  const geaendert =
    title !== ansage.title ||
    text !== ansage.text ||
    JSON.stringify(tipp) !== JSON.stringify(ansage.tipp)
  const tippSetzen = <K extends keyof AnsageTipp>(k: K, v: AnsageTipp[K]) =>
    setTipp((t) => ({ ...t, [k]: v }))

  async function kopieren() {
    try {
      await navigator.clipboard.writeText(ansage.url)
      setKopiert(true)
      setTimeout(() => setKopiert(false), 2000)
    } catch {
      window.prompt('Link kopieren:', ansage.url)
    }
  }

  return (
    <li className="card space-y-3 p-5" data-testid="ansage">
      <input
        className="input font-medium"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Titel (optional)"
        aria-label="Titel"
        disabled={busy}
      />
      <textarea
        className="input min-h-[6rem]"
        value={text}
        onChange={(e) => setText(e.target.value)}
        aria-label="Text"
        disabled={busy}
      />
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <a
          href={ansage.url}
          target="_blank"
          rel="noreferrer"
          className="min-w-0 flex-1 truncate font-mono text-xs text-text-muted underline"
        >
          {ansage.url}
        </a>
        <button type="button" className="btn-chip" onClick={() => void kopieren()}>
          {kopiert ? 'Kopiert' : 'Link kopieren'}
        </button>
      </div>
      <details className="rounded-xl border border-border p-3" open={tipp.aktiv}>
        <summary className="cursor-pointer text-sm font-medium">
          Auslosung nach der Ansage {tipp.aktiv ? '(an)' : '(aus)'}
        </summary>
        <div className="mt-3 space-y-3 text-sm">
          <p className="text-xs text-text-muted">
            Nach dem Text zeigt K4ia beide Mannschaften, ein Lauflicht wird langsamer und bleibt auf
            dem Ergebnis stehen, das du hier festlegst. Logos lädst du selbst hoch (PNG mit
            Transparenz, kein SVG) — die Vereinslogos sind Marken, sie liegen nicht im Code.
          </p>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={tipp.aktiv}
              onChange={(e) => tippSetzen('aktiv', e.target.checked)}
              disabled={busy}
            />
            Auslosung zeigen
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            {(['a', 'b'] as const).map((seite) => (
              <div key={seite} className="space-y-2 rounded-lg border border-border p-3">
                <input
                  className="input"
                  placeholder={
                    seite === 'a'
                      ? 'Mannschaft A, z. B. FC Bayern München'
                      : 'Mannschaft B, z. B. Borussia Dortmund'
                  }
                  aria-label={`Mannschaft ${seite.toUpperCase()}`}
                  value={seite === 'a' ? tipp.teamA : tipp.teamB}
                  onChange={(e) => tippSetzen(seite === 'a' ? 'teamA' : 'teamB', e.target.value)}
                  disabled={busy}
                />
                <div className="flex items-center gap-3">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white">
                    {(seite === 'a' ? ansage.teamALogo : ansage.teamBLogo) ? (
                      <img
                        src={(seite === 'a' ? ansage.teamALogo : ansage.teamBLogo) ?? ''}
                        alt=""
                        className="max-h-14 max-w-14 object-contain"
                      />
                    ) : (
                      <span className="text-[0.65rem] text-neutral-400">kein Logo</span>
                    )}
                  </div>
                  <FileButton
                    label="Logo hochladen"
                    accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
                    disabled={busy}
                    onFile={(file) => onLogo(seite, file)}
                  />
                  {(seite === 'a' ? ansage.teamALogo : ansage.teamBLogo) && (
                    <button
                      type="button"
                      className="btn-chip-danger"
                      disabled={busy}
                      onClick={() => void onLogoEntfernen(seite)}
                    >
                      Logo entfernen
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            <label className="flex flex-col gap-1">
              Ergebnis der Auslosung
              <select
                className="input"
                value={tipp.gewinner}
                onChange={(e) => tippSetzen('gewinner', e.target.value as TippGewinner)}
                disabled={busy}
              >
                {TIPP_GEWINNER.map((g) => (
                  <option key={g} value={g}>
                    {g === 'a'
                      ? `${tipp.teamA || 'Mannschaft A'} gewinnt`
                      : g === 'b'
                        ? `${tipp.teamB || 'Mannschaft B'} gewinnt`
                        : 'Unentschieden'}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1">
              Spielstand (optional)
              <input
                className="input w-28"
                placeholder="2:1"
                value={tipp.ergebnis}
                onChange={(e) => tippSetzen('ergebnis', e.target.value)}
                disabled={busy}
              />
            </label>
          </div>
          <label className="flex flex-col gap-1">
            Was K4ia nach der Auslosung sagt (optional)
            <textarea
              className="input min-h-[3rem]"
              placeholder="Mein Tipp: Bayern gewinnt zwei zu eins."
              value={tipp.text}
              onChange={(e) => tippSetzen('text', e.target.value)}
              disabled={busy}
            />
          </label>
        </div>
      </details>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="btn-primary"
          disabled={busy || !geaendert || !text.trim()}
          onClick={() =>
            void onSpeichern({
              title: title.trim(),
              text: text.trim(),
              tipp: { ...tipp, teamA: tipp.teamA.trim(), teamB: tipp.teamB.trim() },
            })
          }
        >
          Speichern
        </button>
        <button
          type="button"
          className="btn-secondary"
          disabled={busy || !sprachausgabe || geaendert}
          title={
            geaendert
              ? 'Erst speichern'
              : 'Die Tonspur jetzt erzeugen, damit der Link sofort spricht'
          }
          onClick={() => void onTon()}
        >
          Ton vorbereiten
        </button>
        <button
          type="button"
          className="btn-chip-danger ml-auto"
          disabled={busy}
          onClick={onLoeschen}
        >
          Löschen
        </button>
      </div>
    </li>
  )
}
